
import Vue from 'vue';
import TagService from '@/services/TagService';
import UserService from '@/services/UserService';
import { autoComplete } from '@/utils/Util';

export default Vue.extend({
  name: 'RuleActionDialog',

  props: {
    value: {
      type: Object,
      required: false,
    },
  },

  data() {
    const vm = this as any;
    const initialActionType = vm.value?.action || 'add-tag';

    return {
      localDialog: false,
      valid: true,

      localAction: {
        action: initialActionType,
      } as any,

      tagId: vm.value?.data?.tagId || '',
      recipientsInput:
        vm.value?.data?.alert?.attributes?.recipients?.join(', ') || '',
      maxAge: vm.value?.data?.alert?.attributes?.maxAge || 1,
      permission: vm.value?.data?.permission?.permission || 'r',
      userIds: vm.value?.data?.permission?.userId
        ? vm.value.data.permission.userId
        : ([] as string[]),

      tags: [] as any[],
      users: [] as any[],
      loadingOptions: false,

      actionOptions: [
        { text: vm.$t('rule.action.addAlert') as string, value: 'add-alert' },
        { text: vm.$t('rule.action.addTag') as string, value: 'add-tag' },
        {
          text: vm.$t('rule.action.addPermission') as string,
          value: 'add-permission',
        },
      ],

      permissionOptions: ['r', 'rw'],

      conditionType: vm.value?.condition?.regex
        ? 'regex'
        : vm.value?.condition?.dsl
        ? 'dsl'
        : 'none',

      conditionAttribute: vm.value?.condition?.regex?.attribute || '',
      conditionRegex: vm.value?.condition?.regex?.regex || '',
      conditionDsl: vm.value?.condition?.dsl?.dsl || '',

      conditionTypeOptions: [
        { text: vm.$t('rule.condition.none') as string, value: 'none' },
        { text: vm.$t('rule.condition.regexShort') as string, value: 'regex' },
        { text: vm.$t('rule.condition.dslShort') as string, value: 'dsl' },
      ],

      requiredRule: (value: any) => !!value || (vm.$t('required') as string),
      userIdsRule: (value: any) =>
        (Array.isArray(value) ? value.length > 0 : !!value) ||
        (vm.$t('required') as string),
      requiredPositiveNumberRule: (value: any) =>
        (!!value && Number(value) > 0) || (vm.$t('required') as string),
    };
  },

  methods: {
    autoComplete,

    async loadOptions() {
      this.loadingOptions = true;
      try {
        this.tags = await TagService.getAllCollectionPages(this.currentTenant);
        this.users = await UserService.getAllCollectionPages({
          tenant: this.currentTenant,
        });
      } catch (e) {
        console.error('Failed to load tag/user options', e);
      } finally {
        this.loadingOptions = false;
      }
    },

    dismiss() {
      this.localDialog = false;
    },

    confirm() {
      const form: any = this.$refs.form;
      if (form && !form.validate()) return;

      const actionType = this.localAction.action as string;
      const clean: any = { action: actionType, data: {} };

      if (actionType === 'add-tag') {
        clean.data.tagId = this.tagId;
      }

      if (actionType === 'add-alert') {
        const recipients = this.recipientsInput
          .split(',')
          .map((recipient: string) => recipient.trim())
          .filter((recipient: string) => !!recipient);

        clean.data.alert = {
          type: 'generic',
          attributes: {
            recipients,
            maxAge: this.maxAge,
          },
        };
      }

      const condition: any = {};
      if (this.conditionType === 'regex') {
        condition.regex = {
          attribute: this.conditionAttribute,
          regex: this.conditionRegex,
        };
      } else if (this.conditionType === 'dsl') {
        condition.dsl = {
          dsl: this.conditionDsl,
        };
      }

      if (Object.keys(condition).length > 0) {
        clean.condition = condition;
      }

      if (actionType === 'add-permission') {
        const ids = Array.isArray(this.userIds)
          ? this.userIds
          : [this.userIds];

        for (const uid of ids) {
          const action = JSON.parse(JSON.stringify(clean));
          action.data.permission = {
            permission: this.permission,
            userId: uid,
          };
          this.$emit('save', action);
        }
      } else {
        this.$emit('save', clean);
      }

      this.dismiss();
    },
  },

  watch: {
    value(newVal) {
      if (!newVal) return;

      this.localAction.action = newVal.action;
      this.tagId = newVal.data?.tagId || '';
      this.recipientsInput =
        newVal.data?.alert?.attributes?.recipients?.join(', ') || '';
      this.maxAge = newVal.data?.alert?.attributes?.maxAge || 1;
      this.permission = newVal.data?.permission?.permission || 'r';
      this.userIds = newVal.data?.permission?.userId || '';

      const cond = newVal.condition;
      if (cond?.regex) {
        this.conditionType = 'regex';
        this.conditionAttribute = cond.regex.attribute || '';
        this.conditionRegex = cond.regex.regex || '';
        this.conditionDsl = '';
      } else if (cond?.dsl) {
        this.conditionType = 'dsl';
        this.conditionDsl = cond.dsl.dsl || '';
        this.conditionAttribute = '';
        this.conditionRegex = '';
      } else {
        this.conditionType = 'none';
        this.conditionAttribute = '';
        this.conditionRegex = '';
        this.conditionDsl = '';
      }
    },

    localDialog(val: boolean) {
      if (val) this.loadOptions();
    },
  },

  computed: {
    isEdit(): boolean {
      return !!this.value;
    },
    currentTenant(): string {
      return this.$root.$store.state.session.selectedTenant.uuid;
    },
  },
});
